import { Box, Stack, Text, chakra, Flex, Image } from '@chakra-ui/react';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { deleteRecord, newRecord } from 'assets/images';
import EditLog from 'components/EditLog';
import Title from 'components/Title';
import { EDIT_LOG_CODE } from 'global/appConstants';
import { formatYearMonthDayTime, formatDistanceUnit } from 'utils/format';
import { getLabelValue, isDateString } from 'utils/helpers';

interface ITimesheetEditLogs {
  updateLogs: any[];
}

const TimesheetEditLogs: React.FC<ITimesheetEditLogs> = ({ updateLogs = [] }) => {
  const { t } = useTranslation(['timesheetMileage']);

  if (updateLogs.length === 0) {
    return <div></div>;
  }

  interface IUser {
    firstName?: string;
    lastName?: string;
  }

  const formatEditor = (user: IUser): string => {
    if (!user) {
      return t('timesheetMileage:timeeroSystem');
    }

    return `${user.firstName} ${user.lastName}`;
  };

  return (
    <Stack mt={4}>
      <Title>{t('timesheetMileage:activityHistory')}</Title>
      {updateLogs.map((item: any, index: number) => (
        <EditLog
          name={formatEditor(item?.user)}
          isLast={index === updateLogs.length - 1}
          heading={
            <TimeLogHeading
              heading={EDIT_LOG_CODE[item?.action_type_id as keyof typeof EDIT_LOG_CODE]}
              editPerson={formatEditor(item?.user)}
              time={item?.edited_at_human}
            />
          }
          key={index}
        >
          <TimeLogBody oldValues={item?.entity_previous_value} newValues={item?.entity_new_value} />
        </EditLog>
      ))}
    </Stack>
  );
};

interface ITimeLogHeading {
  time: string;
  editPerson: string;
  heading: string;
}

const TimeLogHeading: React.FC<ITimeLogHeading> = ({ time, editPerson, heading }) => {
  const { t } = useTranslation(['common']);
  return (
    <Box>
      <Text fontWeight="bold" fontSize="md">
        {heading}
      </Text>
      <Text color="gray.500" fontSize="sm">
        {time} {t('common:by')} <chakra.span fontWeight="bold">{editPerson}</chakra.span>
      </Text>
    </Box>
  );
};

interface ITimeLogBody {
  oldValues: any;
  newValues: any;
}

const TimeLogBody: React.FC<ITimeLogBody> = ({ oldValues, newValues }) => {
  const previousValues: any[] = getLabelValue(oldValues);
  const currentValues: any[] = getLabelValue(newValues);

  const { t } = useTranslation(['common', 'timesheetMileage']);

  const formatValue = (value: string, isNotes?: boolean, isMileage?: boolean): string => {
    let result = '';

    if (!value) {
      return t('timesheetMileage:notSet');
    }

    if (!isNaN(+value)) {
      if (isMileage) {
        return formatDistanceUnit(parseFloat(value));
      }

      return value;
    }

    if (!isNotes && isDateString(value)) {
      if (format(new Date(value), 'MM-dd-yyyy') !== 'Invalid Date') {
        result = formatYearMonthDayTime(value);
      } else {
        result = value;
      }
    } else {
      result = value;
    }

    if (!result) {
      result = ' ';
    }

    return result;
  };

  return (
    <Box ml={-4} bg="gray.100" w="full" borderRadius="lg">
      <Flex p={3} alignItems="center" w="full">
        <Box w="full">
          {previousValues.length > 0 ? (
            previousValues[0]?.label ? (
              previousValues.map((item: { label: string; value: string }, index: number) => (
                <Box p={2} key={index}>
                  <Text fontWeight="bold">
                    {item.label +
                      ': ' +
                      formatValue(
                        item.value,
                        previousValues[index]?.label?.toLowerCase() === 'notes',
                        previousValues[index]?.label?.toLowerCase() === 'mileage'
                      )}
                  </Text>
                </Box>
              ))
            ) : (
              <Box p={2}>
                <Text fontWeight="bold">{previousValues[0] + ': ' + formatValue(previousValues[1])}</Text>
              </Box>
            )
          ) : (
            <Flex flexDir="column" alignItems="center">
              <Image src={newRecord} boxSize="36px" objectFit="contain" />
              <Text fontWeight="bold" color="success.500">
                {t('common:newRecordCreated')}
              </Text>
            </Flex>
          )}
        </Box>
        <Box w="full">
          {currentValues.length > 0 ? (
            currentValues[0]?.label ? (
              currentValues.map((item: { label: string; value: string }, index: number) => (
                <Box
                  key={index}
                  w="full"
                  p={2}
                  bg="secondary.100"
                  color="secondary.500"
                  border="1px solid"
                  borderColor="secondary.500"
                  borderRadius="base"
                >
                  <Text fontWeight="bold">
                    {item.label +
                      ': ' +
                      formatValue(
                        item.value,
                        previousValues[index]?.label?.toLowerCase() === 'notes',
                        previousValues[index]?.label?.toLowerCase() === 'mileage'
                      )}
                  </Text>
                </Box>
              ))
            ) : (
              <Box
                w="full"
                p={2}
                bg="secondary.100"
                color="secondary.500"
                border="1px solid"
                borderColor="secondary.500"
                borderRadius="base"
              >
                <Text fontWeight="bold">{currentValues[0] + ': ' + formatValue(currentValues[1])}</Text>
              </Box>
            )
          ) : (
            <Flex flexDir="column" alignItems="center">
              <Image src={deleteRecord} boxSize="36px" objectFit="contain" />
              <Text fontWeight="bold" color="secondary.700">
                {t('common:recordDeleted')}
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default TimesheetEditLogs;
